import type { AiSuggestion } from '../../components/AiSuggestionsFab.js';
import {
  CLEANING_FREQ_HOURS,
  type StorageUnitLike,
  type TempReadingLike,
} from './overdue.js';

export interface ProbeAlertRow {
  id: string;
  payload: { storage_unit_id: string; severity: string; message: string };
}

export interface CleaningScheduleRow {
  id: string;
  title: string;
  frequency: string;
  assigned_personnel_ids?: string[] | null;
}

export interface CleaningEventRow {
  payload: { schedule_id: string };
  client_ts: string;
}

export interface FoodRow {
  id: string;
  name: string;
  default_uom?: string | null;
}

export interface StockEventRow {
  payload: { food_id: string; qty: number; expiration_date: string | null };
}

export interface SuggestedStockBalance {
  foodId: string;
  batchLot: string | null;
  expirationDate: string | null;
  onHandQty: number;
}

export interface StorageUnitRow extends StorageUnitLike {
  name: string;
  assigned_personnel_ids?: string[] | null;
}

// Floor di recency: un lotto scaduto da oltre 24h NON è più "in scadenza"
// imminente e — con magazzino OFF, dove la qty non viene mai scaricata — resterebbe
// segnalato all'infinito senza modo di spegnerlo. Lo escludiamo così l'avviso si
// spegne da solo dopo la scadenza (stessa lezione di T-249 sugli alert append-only).
const EXPIRED_GRACE_MS = 24 * 3_600_000;

export interface SuggestionsInput {
  units: StorageUnitRow[];
  schedules: CleaningScheduleRow[];
  foods: FoodRow[];
  tempReads: (TempReadingLike & { client_ts: string })[];
  cleanEvents: CleaningEventRow[];
  alerts: ProbeAlertRow[];
  stockIn?: StockEventRow[];
  stockOut?: StockEventRow[];
  stockBalances?: SuggestedStockBalance[];
  now: number;
  expirationLeadHours?: number;
  // Giorni di chiusura del business attivo (lun/mar/mer/gio/ven/sab/dom).
  // Quando "oggi" è un giorno di chiusura, le pulizie/temperature non vengono
  // marcate come overdue per evitare falsi positivi durante chiusure regolari.
  giorni_chiusura?: string[];
}

const WEEKDAY_KEYS = ['dom', 'lun', 'mar', 'mer', 'gio', 'ven', 'sab'];

function isClosedToday(now: number, giorniChiusura: string[] | undefined): boolean {
  if (!giorniChiusura || giorniChiusura.length === 0) return false;
  const todayKey = WEEKDAY_KEYS[new Date(now).getDay()];
  return giorniChiusura.includes(todayKey);
}

export function computeSuggestions(input: SuggestionsInput): AiSuggestion[] {
  const {
    units,
    schedules,
    foods,
    tempReads,
    cleanEvents,
    alerts,
    stockIn = [],
    stockOut = [],
    stockBalances,
    now,
    expirationLeadHours = 48,
    giorni_chiusura,
  } = input;
  const out: AiSuggestion[] = [];
  const closed = isClosedToday(now, giorni_chiusura);

  for (const a of alerts.slice(0, 3)) {
    const unit = units.find((u) => u.id === a.payload.storage_unit_id);
    out.push({
      id: `alert-${a.id}`,
      title: `Allerta ${unit?.name ?? a.payload.storage_unit_id.slice(0, 8)}`,
      detail: a.payload.message,
      severity: a.payload.severity === 'critical' ? 'danger' : 'warning',
      href: '/haccp/temperatures',
    });
  }

  const lastDoneByScheduleMs = new Map<string, number>();
  for (const e of cleanEvents) {
    const ts = Date.parse(e.client_ts);
    const cur = lastDoneByScheduleMs.get(e.payload.schedule_id);
    if (cur === undefined || ts > cur) lastDoneByScheduleMs.set(e.payload.schedule_id, ts);
  }
  const overdueTasks = schedules.filter((s) => {
    const max = CLEANING_FREQ_HOURS[s.frequency] ?? 24;
    if (!Number.isFinite(max)) return false;
    const last = lastDoneByScheduleMs.get(s.id);
    return !last || (now - last) / 3_600_000 >= max;
  });
  // In giorno di chiusura non segnalare pulizie in ritardo: il locale è chiuso e nessuno
  // può pulire — sarebbe un falso positivo che affolla il banner suggerimenti.
  if (overdueTasks.length > 0 && !closed) {
    out.push({
      id: 'overdue-cleaning',
      title: `${overdueTasks.length} pulizie in ritardo`,
      detail: overdueTasks.slice(0, 2).map((s) => s.title).join(', '),
      severity: 'warning',
      href: '/haccp/cleaning/todo',
    });
  }

  const lots = new Map<string, { foodId: string; qty: number; expires: string | null }>();
  if (stockBalances) {
    for (const balance of stockBalances) {
      const key = `${balance.foodId}|${balance.batchLot ?? ''}`;
      lots.set(key, {
        foodId: balance.foodId,
        qty: balance.onHandQty,
        expires: balance.expirationDate,
      });
    }
  } else {
    for (const e of stockIn) {
      const key = e.payload.food_id;
      const cur = lots.get(key) ?? {
        foodId: e.payload.food_id,
        qty: 0,
        expires: e.payload.expiration_date,
      };
      cur.qty += e.payload.qty;
      if (!cur.expires) cur.expires = e.payload.expiration_date;
      lots.set(key, cur);
    }
    for (const e of stockOut) {
      const cur = lots.get(e.payload.food_id);
      if (cur) cur.qty -= e.payload.qty;
    }
  }
  const expiring: { food_id: string; product: string; date: string }[] = [];
  for (const l of lots.values()) {
    if (l.qty <= 0 || !l.expires) continue;
    const ms = Date.parse(l.expires) - now;
    // In scadenza entro la finestra scelta OPPURE scaduto da meno di 24h. I lotti scaduti da più
    // tempo (qty mai azzerata col magazzino off) NON vengono più segnalati.
    if (ms < expirationLeadHours * 3_600_000 && ms > -EXPIRED_GRACE_MS) {
      expiring.push({
        food_id: l.foodId,
        product: foods.find((f) => f.id === l.foodId)?.name ?? l.foodId.slice(0, 8),
        date: l.expires,
      });
    }
  }
  if (expiring.length > 0) {
    out.push({
      id: 'expiring',
      title: `${expiring.length} lotti in scadenza`,
      detail: expiring.slice(0, 2).map((e) => e.product).join(', '),
      severity: 'danger',
      href: '/haccp/expirations',
    });
  }

  const latestByUnit = new Map<string, number>();
  for (const r of tempReads) {
    const ts = Date.parse(r.client_ts);
    const cur = latestByUnit.get(r.payload.storage_unit_id);
    if (cur === undefined || ts > cur) latestByUnit.set(r.payload.storage_unit_id, ts);
  }
  const unitsWithoutToday = units.filter((u) => {
    const last = latestByUnit.get(u.id);
    const freq = u.check_frequency_hours ?? 24;
    return !last || (now - last) / 3_600_000 > freq;
  });
  // Stessa logica per le temperature: in giorno di chiusura nessuno apre il locale
  // per fare le rilevazioni → no warning.
  if (unitsWithoutToday.length > 0 && !closed) {
    out.push({
      id: 'missing-temp',
      title: `${unitsWithoutToday.length} frigo senza lettura recente`,
      detail: unitsWithoutToday.slice(0, 2).map((u) => u.name).join(', '),
      severity: 'warning',
      href: '/haccp/temperatures',
    });
  }

  return out;
}
